import { CircularProgress, Icon, Slider, Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/styles'
import CancelButton from 'app/components/buttons/CancelButton'
import PrimaryButton from 'app/components/buttons/PrimaryButton'
import ConfirmationDialog from 'app/components/ConfirmationDialog'
import clsx from 'clsx'
import { compressImage } from 'libs/compressImage'
import PropTypes from 'prop-types'
import * as R from 'ramda'
import React, { useCallback, useRef, useState } from 'react'
import AvatarEditor from 'react-avatar-editor'
import { FormattedMessage, useIntl } from 'react-intl'

import AvatarWithFallback from '../AvatarWithFallback'

const useStyles = makeStyles((theme) => ({
  root: {
    '& #hover-area:hover + label': {
      top: '0% !important',
    },
    '& label:not(:hover)': {
      top: '100%',
    },
  },
  link: {
    color: theme.palette.primary.main,
  },
  text: {
    fontSize: 12,
    color: theme.palette.primary.text,
  },
  icon: {
    color: theme.palette.primary.text,
    fontSize: 18,
  },
  uploadButton: {
    borderBottomLeftRadius: '48px',
    borderBottomRightRadius: '48px',
    backgroundColor: 'rgb(28,30,33, 0.8)',
  },
  loadingBackground: {
    borderRadius: '48px',
    backgroundColor: 'rgb(28,30,33, 0.8)',
  },
}))

const INITIAL_ZOOM = 1
const MAX_ZOOM = 4
const MIN_ZOOM = 0.3
const STEP_ZOOM = 0.1
const EDITOR_WIDTH = 250
const IMAGE_OUTPUT_QUALITY = 0.8
const UploadEditableAvatar = ({
  src,
  onChange,
  isLoading = false,
  disabled = false,
  className,
  ...rest
}) => {
  const classes = useStyles(rest)
  const imageEditor = useRef()
  const [imageFile, setImageFile] = useState()
  const [zoom, setZoom] = useState(INITIAL_ZOOM)
  const [randomName] = useState(Math.random())
  const { formatMessage } = useIntl()
  const inputEl = useRef(null)

  const handleClose = useCallback(() => {
    setImageFile(null)
    setZoom(INITIAL_ZOOM)
    inputEl.current.value = null
  }, [])

  const compressImageCallback = useCallback(() => {
    const canvas = imageEditor.current.getImage()
    canvas.toBlob(
      (blob) => {
        blob.name = imageFile.name
        compressImage(blob)
          .then((result) => {
            // I don't know what this line does, Maybe I'll find out in PR
            result.name = imageFile.name
            // When upload fails. No handle?
            onChange(result).then(handleClose)
          })
          .catch((err) => {
            // Handle?
            console.log(err.message)
          })
      },
      imageFile.type,
      IMAGE_OUTPUT_QUALITY,
    )
  }, [imageEditor, imageFile, onChange, handleClose])

  function renderUploadControl() {
    if (disabled) return null
    return (
      <>
        {isLoading && (
          <div
            className={clsx(
              classes.loadingBackground,
              'absolute flex justify-center items-center w-full h-full overflow-hidden',
            )}
          >
            <CircularProgress className="absolute " />
          </div>
        )}
        <div
          className={clsx(
            classes.root,
            'absolute w-96 h-48 bottom-0 overflow-hidden',
          )}
        >
          <div id="hover-area" className="absolute w-full h-full"></div>
          <label
            htmlFor={randomName}
            className={clsx(
              classes.link,
              'absolute w-full h-full cursor-pointer',
            )}
          >
            <div
              className={clsx(
                classes.uploadButton,
                'flex justify-center items-center w-full h-full',
              )}
            >
              <div className="flex flex-col items-center">
                <Icon className={classes.icon}>photo_camera</Icon>
                <Typography className={classes.text}>
                  <FormattedMessage id="action.update" />
                </Typography>
              </div>
            </div>
          </label>
          <input
            type="file"
            id={randomName}
            ref={inputEl}
            accept="image/png, image/jpeg"
            className="invisible absolute"
            onChange={R.compose(
              R.unless(R.isNil, setImageFile),
              R.unless(R.isNil, R.head),
              R.view(R.lensPath(['target', 'files'])),
            )}
          />
        </div>
      </>
    )
  }
  return (
    <>
      <div className={clsx('w-96 h-96 relative', className)}>
        <AvatarWithFallback
          src={src}
          className="absolute w-96 h-96"
          {...rest}
        />
        {renderUploadControl()}
      </div>
      <ConfirmationDialog
        open={imageFile}
        title={formatMessage({ id: 'place.logo.update.title' })}
        maxWidth="md"
        onClose={handleClose}
        body={
          <div className="flex justify-center">
            <div className="flex flex-col items-center">
              <AvatarEditor
                ref={(editor) => {
                  imageEditor.current = editor
                }}
                image={imageFile}
                borderRadius={EDITOR_WIDTH / 2}
                width={EDITOR_WIDTH}
                height={EDITOR_WIDTH}
                border={0}
                scale={zoom}
              />
              <div className="mt-16 w-full">
                <Typography>
                  <FormattedMessage id="place.logo.update.zoom" />
                </Typography>
                <Slider
                  value={zoom}
                  step={STEP_ZOOM}
                  max={MAX_ZOOM}
                  min={MIN_ZOOM}
                  onChange={(event, newValue) => setZoom(newValue)}
                  aria-labelledby="continuous-slider"
                />
              </div>
            </div>
          </div>
        }
        CancelButton={imageFile && <CancelButton onClick={handleClose} />}
        ConfirmButton={
          imageFile && (
            <PrimaryButton
              isLoading={isLoading}
              onClick={compressImageCallback}
            >
              <FormattedMessage id="action.update" />
            </PrimaryButton>
          )
        }
      />
    </>
  )
}

UploadEditableAvatar.propTypes = {
  src: PropTypes.string,
  onChange: PropTypes.func,
  isLoading: PropTypes.bool,
  disabled: PropTypes.bool,
}

export default UploadEditableAvatar
